import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { IAccountDiscountInfo } from '@boss/services/client';
import { DiscountTile } from '@boss/ui';
import { getAccountPageSlugByType } from '@boss/utils';

import DiscountDetail from './detail';
import { useAccountDiscountInfo } from '../../../client-queries';
import { DiscountsSkeleton } from '../../../components';
import { DISCOUNT_TYPES } from '../../../constants';
import { getVisibleToDate } from '../../../utils';

type DiscountCard = IAccountDiscountInfo & {
  href?: string;
  validUntilLabel?: string;
};

type DiscountSectionProps = {
  cards: DiscountCard[];
  isLinkable: boolean;
  rgb: string;
  sectionTitle: string;
};

const DiscountSection = ({ cards, isLinkable, rgb, sectionTitle }: DiscountSectionProps) => {
  if (!cards.length) {
    return null;
  }

  return (
    <div>
      <h2 className="mb-5 text-2xl">{sectionTitle}</h2>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {cards.map(card => {
          if (isLinkable) {
            return (
              <DiscountTile
                accentColor={rgb}
                cardTitle={card.title}
                href={card.href}
                key={card.key}
                validUntil={card.validUntilLabel}
                variant="link"
              />
            );
          }

          return (
            <DiscountTile
              accentColor={rgb}
              amount={card.discountLabelInfo}
              cardTitle={card.title}
              key={card.key}
              validUntil={card.validUntilLabel}
              variant="amount"
            />
          );
        })}
      </div>
    </div>
  );
};

const DiscountsOverview = ({ discountGroupIds }: { discountGroupIds?: string[] }) => {
  const { locale } = useRouter();
  const { t } = useTranslation('account');

  const localeStr = locale as string;
  const baseSlug = getAccountPageSlugByType('DISCOUNT', localeStr) ?? '';

  const { data: defaultDiscounts, isLoading: defaultDiscountsLoading } = useAccountDiscountInfo(
    localeStr,
    DISCOUNT_TYPES.DEFAULT.code,
    discountGroupIds,
  );
  const { data: specificDiscounts, isLoading: specificDiscountsLoading } = useAccountDiscountInfo(
    localeStr,
    DISCOUNT_TYPES.SPECIFIC.code,
    discountGroupIds,
  );
  const { data: worksiteDiscounts, isLoading: worksiteDiscountsLoading } = useAccountDiscountInfo(
    localeStr,
    DISCOUNT_TYPES.WORKSITE.code,
    discountGroupIds,
  );

  /**
   * Build enriched card objects with href and validUntilLabel resolved here
   * so DiscountSection stays presentation-only.
   */
  const buildCards = (discounts: IAccountDiscountInfo[] | undefined, sectionTitle: string): DiscountCard[] =>
    (discounts ?? []).map((discount, index) => {
      const visibleDate = getVisibleToDate(discount.toDate);

      return {
        ...discount,
        key: discount.key ?? `${discount.title}-${index}`,
        href: discount.key
          ? `${baseSlug}/${encodeURIComponent(discount.key)}?title=${encodeURIComponent(
              discount.title,
            )}&sectionTitle=${encodeURIComponent(sectionTitle)}`
          : undefined,
        validUntilLabel: visibleDate ? (t('discount.validUntil', { date: visibleDate }) as string) : undefined,
      };
    });

  const sections = [
    {
      cards: buildCards(defaultDiscounts, t('discount.default')),
      isLinkable: false,
      rgb: DISCOUNT_TYPES.DEFAULT.rgb,
      sectionTitle: t('discount.default'),
    },
    {
      cards: buildCards(specificDiscounts, t('discount.specific')),
      isLinkable: true,
      rgb: DISCOUNT_TYPES.SPECIFIC.rgb,
      sectionTitle: t('discount.specific'),
    },
    {
      cards: buildCards(worksiteDiscounts, t('discount.worksite')),
      isLinkable: true,
      rgb: DISCOUNT_TYPES.WORKSITE.rgb,
      sectionTitle: t('discount.worksite'),
    },
  ];

  // Gate everything on a single loading flag: show skeleton until ALL queries resolve.
  const isLoading = defaultDiscountsLoading || specificDiscountsLoading || worksiteDiscountsLoading;
  const allEmpty = sections.every(s => !s.cards.length);

  return (
    <div className="mb-20">
      <h1 className="mb-5">{t('discount.title')}</h1>
      {isLoading ? (
        <DiscountsSkeleton />
      ) : (
        <div className="flex flex-col gap-7">
          {allEmpty && <p>{t('discount.noDiscounts')}</p>}
          {sections.map(section => (
            <DiscountSection key={section.sectionTitle} {...section} />
          ))}
        </div>
      )}
    </div>
  );
};

const Discounts = ({ discountGroupIds }: { discountGroupIds?: string[] }) => {
  const { asPath } = useRouter();

  const rawKey = asPath.split('/')[3]?.split('?')[0];
  const discountKey = rawKey ? decodeURIComponent(rawKey) : undefined;

  return discountKey ? (
    <DiscountDetail discountGroupIds={discountGroupIds} discountKey={discountKey} />
  ) : (
    <DiscountsOverview discountGroupIds={discountGroupIds} />
  );
};

export default Discounts;
